(function () {
  'use strict';

  const RETRY_DELAY = 500;
  const RETRY_LIMIT = 20;
  const UPDATE_EVENT = 'bss_b2b:CustomCartUpdate';
  const previous = window.UpvanityBssCartBridge;
  previous?.destroy?.();

  let active = true;
  let timer;
  let retryCount = 0;
  let needsBinding = true;
  let pendingCart = null;
  let listenerSubject = null;
  let listenerReady = false;
  let listenerInstalling = false;
  let generation = 0;
  const observedRoots = new Map();

  function configureProductSelectors() {
    const productPage = window.BSS_BCP_V2?.productPage;
    if (!productPage) return;
    productPage.addCartSelectors = ['.add_to_cart'];
    productPage.productInfoSelectors = ['.yv-main-product-detail'];
  }

  function schedule(delay = 0) {
    if (!active) return;
    clearTimeout(timer);
    timer = setTimeout(flush, delay);
  }

  function retry() {
    if (!active || retryCount >= RETRY_LIMIT) return;
    retryCount += 1;
    schedule(RETRY_DELAY);
  }

  function ensureUpdateListener(subject) {
    if (listenerSubject !== subject) {
      listenerSubject = subject;
      listenerReady = false;
      listenerInstalling = false;
    }
    if (listenerReady || listenerInstalling) return;
    if (window.bssB2BAddedEvent?.[UPDATE_EVENT]) {
      listenerReady = true;
      return;
    }
    if (typeof subject.listenCartUpdateCustom !== 'function') {
      retry();
      return;
    }
    listenerInstalling = true;
    const owner = generation;
    let result;
    try {
      result = subject.listenCartUpdateCustom();
    } catch (error) {
      listenerInstalling = false;
      retry();
      return;
    }
    if (window.bssB2BAddedEvent?.[UPDATE_EVENT]) {
      listenerReady = true;
      listenerInstalling = false;
    }
    Promise.resolve(result).then(() => {
      if (!active || owner !== generation || listenerSubject !== subject) return;
      listenerInstalling = false;
      listenerReady = true;
      if (pendingCart) flush();
    }, () => {
      if (!active || owner !== generation || listenerSubject !== subject) return;
      listenerInstalling = false;
      retry();
    });
  }

  function flush() {
    clearTimeout(timer);
    timer = undefined;
    if (!active) return;
    configureProductSelectors();
    const subject = window.BSS_B2B?.observer?.cartSubject;
    if (typeof subject?.listenCartCheckout !== 'function') {
      retry();
      return;
    }
    if (needsBinding) {
      needsBinding = false;
      const owner = generation;
      try {
        Promise.resolve(subject.listenCartCheckout()).catch(() => {
          if (!active || owner !== generation) return;
          needsBinding = true;
          retry();
        });
      } catch (error) {
        needsBinding = true;
        retry();
      }
    }
    ensureUpdateListener(subject);
    if (!pendingCart || !listenerReady) return;
    const cart = pendingCart;
    pendingCart = null;
    document.dispatchEvent(new CustomEvent(UPDATE_EVENT, {
      detail: { cart }
    }));
  }

  function observeRoots() {
    for (const [root, observer] of observedRoots) {
      if (root.isConnected === false) {
        observer.disconnect();
        observedRoots.delete(root);
      }
    }
    document.querySelectorAll(
      '.yv-side-drawer-container, [data-cart-wrapper]'
    ).forEach((root) => {
      if (observedRoots.has(root)) return;
      const observer = new MutationObserver(() => {
        needsBinding = true;
        schedule();
      });
      observer.observe(root, { childList: true });
      observedRoots.set(root, observer);
    });
  }

  function rendered(event) {
    if (!active) return;
    const cart = event.detail?.cart;
    pendingCart = cart && Array.isArray(cart.items) &&
      Number.isFinite(cart.item_count) && Number.isFinite(cart.total_price)
      ? cart : null;
    retryCount = 0;
    needsBinding = true;
    observeRoots();
    flush();
  }

  function moduleLoaded() {
    if (!active) return;
    retryCount = 0;
    needsBinding = true;
    observeRoots();
    schedule();
  }

  function suspend() {
    active = false;
    generation += 1;
    clearTimeout(timer);
    observedRoots.forEach((observer) => observer.disconnect());
    observedRoots.clear();
    pendingCart = null;
    listenerSubject = null;
    listenerReady = false;
    listenerInstalling = false;
  }

  function pageShown(event) {
    if (!event.persisted) return;
    active = true;
    moduleLoaded();
  }

  function destroy() {
    suspend();
    document.removeEventListener('upv:cart-rendered', rendered);
    window.removeEventListener('bss_b2b:module:loaded', moduleLoaded);
    window.removeEventListener('load', moduleLoaded);
    window.removeEventListener('pagehide', suspend);
    window.removeEventListener('pageshow', pageShown);
  }

  window.UpvanityBssCartBridge = { destroy };
  document.addEventListener('upv:cart-rendered', rendered);
  window.addEventListener('bss_b2b:module:loaded', moduleLoaded);
  window.addEventListener('load', moduleLoaded);
  window.addEventListener('pagehide', suspend);
  window.addEventListener('pageshow', pageShown);
  observeRoots();
  schedule();
})();
